import React from 'react';
import { Search, X, Layers, Package, Globe, Play, Gamepad2 } from 'lucide-react';
import { useFilter } from '../../context/FilterContext';
import { useSite } from '../../context/SiteContext';
import { defaultSiteData } from '../../mock';

export default function SearchResultsBar() {
  const { query, reset, matches } = useFilter();
  const { data } = useSite();

  const q = (query || '').trim().toLowerCase();
  if (!q) return null;

  // Calculate matching counts across site sections
  const gameCards = (data.gameCards || []).filter(matches);
  
  let productMatchesCount = 0;
  (data.products || []).forEach(p => {
    (p.items || []).forEach(it => {
      if (matches(it)) productMatchesCount++;
    });
  });

  // Exchange Listings
  const el = data.exchangeListings || {};
  const defaultTypes = defaultSiteData.exchangeListings?.types || [];
  const configuredTypes = (el.types && el.types.length > 0) ? el.types : defaultTypes;
  let exchangeMatchesCount = 0;
  configuredTypes.forEach(t => {
    (t.items || []).forEach(it => {
      if (matches(it)) exchangeMatchesCount++;
    });
  });

  // InPlay GET ID Platforms
  const inPlayMatches = (data.inPlayMatches || []).filter(matches);

  // Originals Platforms Showcase
  const originalsGames = (data.originalsGames || []).filter(matches);

  const totalMatches = gameCards.length + productMatchesCount + exchangeMatchesCount + inPlayMatches.length + originalsGames.length;

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = window.innerWidth < 768 ? 120 : 100;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }
  };

  return (
    <div
      className="w-full bg-gradient-to-r from-amber-400 via-[#ccc77d] to-amber-300 text-black px-3 sm:px-4 py-2 shadow-lg border-b border-black/20"
      data-testid="search-results-bar"
    >
      <div className="max-w-[1440px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 md:gap-4">
        {/* Left info */}
        <div className="flex items-center gap-2 font-bold text-xs sm:text-sm truncate">
          <span className="w-6 h-6 rounded-full bg-black text-[#ccc77d] flex items-center justify-center shrink-0">
            <Search size={13} />
          </span>
          <span className="truncate">
            Showing results for <span className="underline font-extrabold">"{query}"</span>
          </span>
          <span className="px-2 py-0.5 rounded-full text-xs font-extrabold bg-black text-amber-300 shrink-0">
            {totalMatches} {totalMatches === 1 ? 'match' : 'matches'}
          </span>
        </div>

        {/* Section Jump Pills & Clear Button */}
        <div className="flex items-center gap-1.5 flex-wrap justify-center sm:justify-end text-xs">
          {exchangeMatchesCount > 0 && (
            <button
              type="button"
              onClick={() => scrollToSection('exchanges')}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-black/15 hover:bg-black/30 text-black font-extrabold transition-all"
            >
              <Globe size={11} /> Exchanges ({exchangeMatchesCount})
            </button>
          )}
          {productMatchesCount > 0 && (
            <button
              type="button"
              onClick={() => scrollToSection('products')}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-black/15 hover:bg-black/30 text-black font-extrabold transition-all"
            >
              <Package size={11} /> Marketplace ({productMatchesCount})
            </button>
          )}
          {gameCards.length > 0 && (
            <button
              type="button"
              onClick={() => scrollToSection('gamecards')}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-black/15 hover:bg-black/30 text-black font-extrabold transition-all"
            >
              <Layers size={11} /> Services ({gameCards.length})
            </button>
          )}
          {inPlayMatches.length > 0 && (
            <button
              type="button"
              onClick={() => scrollToSection('get-id')}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-black/15 hover:bg-black/30 text-black font-extrabold transition-all"
            >
              <Play size={11} /> GET ID ({inPlayMatches.length})
            </button>
          )}
          {originalsGames.length > 0 && (
            <button
              type="button"
              onClick={() => scrollToSection('platforms')}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-black/15 hover:bg-black/30 text-black font-extrabold transition-all"
            >
              <Gamepad2 size={11} /> Platforms ({originalsGames.length})
            </button>
          )}

          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1 px-3 py-1 rounded-md bg-black hover:bg-zinc-900 text-white font-extrabold transition-all shadow-sm shrink-0"
            data-testid="clear-search-btn"
          >
            <X size={13} /> Clear Search
          </button>
        </div>
      </div>
    </div>
  );
}
